import { BrowserModule } from '@angular/platform-browser';
import { RouterModule } from '@angular/router';
import { APP_INITIALIZER, LOCALE_ID, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NgxDatatableModule } from '@swimlane/ngx-datatable';
import { ModalModule } from 'ngx-bootstrap/modal';
import { ToastrModule } from 'ngx-toastr';
import { TextMaskModule } from 'angular2-text-mask';
import { Angular2PromiseButtonModule } from 'angular2-promise-buttons';
import { CustomFormsModule } from 'ng2-validation';
import { NgxLoadingModule, ngxLoadingAnimationTypes } from 'ngx-loading';
import { FileUploadModule } from 'ng2-file-upload';
import { NouisliderModule } from 'ng2-nouislider';
import { AppComponent } from './app.component';
import { SessionModule } from './session/session.module';
import { DashboardModule } from './dashboard/dashboard.module';
import { AppRoutes } from './app.routes';
import { SharedModule } from './shared/shared.module';
import { LayoutModule } from './layout/layout.module';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { EventBus } from './shared/event/bus';
import { CampaignUtilModule } from './module/campaign-util/campaign-util.module';
import { BreadcrumbModule } from './shared/ui/breadcrumb/breadcrumb.module';
import { AdminModule } from './module/admin/admin.module';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { AccountModule } from './account/account.module';
import { CampaignId } from './module/campaign-util/shared/campaign-id';
import { RecaptchaFormsModule, RecaptchaModule } from 'ng-recaptcha';
import { ErrorModule } from './error/error.module';
import { CampaignEntityService } from './module/campaign-util/shared/campaign-entity';
import { HttpCacheClient } from './shared/http/cache-client';
import { ChartistModule } from 'ng-chartist';
import { GlobalEventBus } from './shared/event/global-bus';
import { NgxMaskModule } from 'ngx-mask';
import { StaticModule } from './static/static.module';
import { ClipboardModule } from 'ngx-clipboard';
import { MomentTimezonePickerModule } from 'moment-timezone-picker';
import { VgCoreModule } from 'videogular2/compiled/src/core/core';
import { VgControlsModule } from 'videogular2/compiled/src/controls/controls';
import { VgOverlayPlayModule } from 'videogular2/compiled/src/overlay-play/overlay-play';
import { VgBufferingModule } from 'videogular2/compiled/src/buffering/buffering';
import { CurrencyPipe, registerLocaleData } from '@angular/common';
import localeDa from '@angular/common/locales/da';
import localeEnUS from '@angular/common/locales/en';
import { TimepickerModule } from 'ngx-bootstrap/timepicker';
import { PhotographerOptionsService } from './module/campaign-util/shared/photographers';
import { SessionTokenService } from './session/service/session-token';
import { QRCodeModule } from 'angularx-qrcode';

export function initializeSession(
  sessionTokenService: SessionTokenService
): () => Promise<void> {
  return () => {
    const expired = !sessionTokenService.isAuthenticated()
      || sessionTokenService.isRefreshTokenExpired();
    if (expired) {
      sessionTokenService.destroy();
    }
    return Promise.resolve();
  };
}

@NgModule({
    declarations: [
        AppComponent,
    ],
    imports: [
        BrowserModule,
        ReactiveFormsModule,
        BrowserAnimationsModule,
        NgxDatatableModule,
        FormsModule,
        TextMaskModule,
        ModalModule.forRoot(),
        TooltipModule.forRoot(),
        CustomFormsModule,
        FileUploadModule,
        RecaptchaModule,
        RecaptchaFormsModule,
        NgxLoadingModule.forRoot({
            animationType: ngxLoadingAnimationTypes.threeBounce,
            backdropBackgroundColour: 'rgba(0,0,0,0.1)',
            backdropBorderRadius: '4px',
            primaryColour: '#333333',
            secondaryColour: '#333333',
            tertiaryColour: '#333333'
        }),
        Angular2PromiseButtonModule.forRoot({
            // your custom config goes here
            spinnerTpl: '<span class="btn-spinner"></span>',
            // disable buttons when promise is pending
            disableBtn: true,
            // the class used to indicate a pending promise
            btnLoadingClass: 'is-loading',
            // only disable and show is-loading class for clicked button,
            // even when they share the same promise
            handleCurrentBtnOnly: false,
        }),
        MomentTimezonePickerModule,
        RouterModule.forRoot(AppRoutes, { enableTracing: false }),
        ToastrModule.forRoot({
            positionClass: 'toast-top-right',
            preventDuplicates: true,
            timeOut: 5000
        }),
        NgxMaskModule.forRoot(),
        NouisliderModule,
        RouterModule,
        SharedModule,
        LayoutModule,
        SessionModule,
        AccountModule,
        StaticModule,
        ErrorModule,
        DashboardModule,
        AdminModule,
        CampaignUtilModule,
        BreadcrumbModule.forRoot(),
        DragDropModule,
        ChartistModule,
        ClipboardModule,
        VgCoreModule,
        VgControlsModule,
        VgOverlayPlayModule,
        VgBufferingModule,
        QRCodeModule,
        TimepickerModule.forRoot()
    ],
    providers: [
        {
            provide: APP_INITIALIZER,
            useFactory: initializeSession,
            deps: [SessionTokenService],
            multi: true
        },
        EventBus,
        GlobalEventBus,
        {
            provide: CampaignId,
            useValue: new CampaignId()
        },
        CampaignEntityService,
        PhotographerOptionsService,
        HttpCacheClient,
        CurrencyPipe
    ],
    bootstrap: [AppComponent]
})
export class AppModule {
    constructor() {
        localeEnUS[17]['CHF'] = ['₣', undefined];
        localeEnUS[17]['SGD'] = ['S$', undefined];
        localeEnUS[17]['THB'] = ['฿', undefined];
        localeEnUS[17]['COP'] = ['$', undefined];
        localeEnUS[17]['CLP'] = ['$', undefined];
        localeEnUS[17]['ARS'] = ['$', undefined];
        registerLocaleData(localeDa, 'da-DK');
        registerLocaleData(localeEnUS, 'en-US');
    }
}
